import { arrayOf, shape, string, func } from 'prop-types';
import React, { Component, Fragment } from 'react';
import dynamic from 'next/dynamic';
import ActionIcon from 'components/ActionIcon';
import Image from 'components/Image';
import Link from 'components/Link';
import Arrow from 'components/Icons/Arrow';
import LogoLink from 'components/LogoLink';
import SpreadBundle from 'components/SpreadBundle';
import { MORE_SECTIONS, FEATURE_LINKS, SOCIAL_LINKS } from 'constants/links';
import { X_SITE_LOGO } from 'constants/logos';
import {
  SITE_HEADER_FEATURES_HEADING,
  X_SITE,
  X_SITE_NAME,
  X_SITE_DESCRIPTION
} from 'constants/site';
import { isMobile } from 'util/viewport';
import navRecirc from 'data/header/navRecirc';
import SiteHeaderNavBlock from './SiteHeaderNavBlock';
import siteHeaderNavStyles from './siteHeaderNavStyles';

const Recirc = dynamic(() => import('components/Recirc'), {
  ssr: false,
  loading: () => null
});

const LINK_STRUCTURE = {
  label: string.isRequired,
  href: string.isRequired,
  as: string,
  target: string,
  rel: string
};

const FEATURES_SOURCE_SIZES = [
  [
    {
      media: '(min-width: 75rem)',
      layoutWidth: {
        value: 400,
        units: 'px'
      }
    },
    {
      media: '(min-width: 64rem)',
      layoutWidth: {
        value: 120,
        units: 'px'
      }
    },
    {
      media: '(min-width: 48rem)',
      layoutWidth: {
        value: 400,
        units: 'px'
      }
    },
    {
      layoutWidth: {
        value: 100,
        units: 'vw'
      }
    }
  ],
  [
    {
      media: '(min-width: 75rem)',
      layoutWidth: {
        value: 400,
        units: 'px'
      }
    },
    {
      media: '(min-width: 64rem)',
      layoutWidth: {
        value: 120,
        units: 'px'
      }
    },
    {
      media: '(min-width: 48rem)',
      layoutWidth: {
        value: 400,
        units: 'px'
      }
    },
    {
      layoutWidth: {
        value: 100,
        units: 'vw'
      }
    }
  ],
  [
    {
      media: '(min-width: 75rem)',
      layoutWidth: {
        value: 400,
        units: 'px'
      }
    },
    {
      media: '(min-width: 64rem)',
      layoutWidth: {
        value: 120,
        units: 'px'
      }
    },
    {
      media: '(min-width: 48rem)',
      layoutWidth: {
        value: 120,
        units: 'px'
      }
    },
    {
      layoutWidth: {
        value: 33,
        units: 'vw'
      }
    }
  ]
];

const getTabLabel = e => e.target.getAttribute('data-tab-label');

const isTouchDevice = process.browser && 'ontouchstart' in document;

class SiteHeaderNav extends Component {
  constructor(props) {
    super(props);

    this.state = {
      // used for lazy loading the Trending posts under each Menu per request
      activatedTabs: new Map(),
      isRecircVisible: !isMobile(1023)
    };

    this.openActiveTabTimeout = null;

    this.handleOrientationChange = this.handleOrientationChange.bind(this);
    this.handleNavMouseEnter = this.handleNavMouseEnter.bind(this);
    this.handleNavMouseLeave = this.handleNavMouseLeave.bind(this);
    this.handleNavClick = this.handleNavClick.bind(this);
    this.handleNavTouchStart = this.handleNavTouchStart.bind(this);
    this.handleNavTouchEnd = this.handleNavTouchEnd.bind(this);

    this.handleNavItemClick = this.handleNavItemClick.bind(this);
    this.handleNavItemTouchStart = this.handleNavItemTouchStart.bind(this);

    this.handleCloseClick = this.handleCloseClick.bind(this);
  }

  componentDidMount() {
    if ('onorientationchange' in window) {
      window.addEventListener(
        'orientationchange',
        this.handleOrientationChange,
        false
      );
    }
  }

  componentWillUnmount() {
    document.body.classList.remove('with-open-nav');
    if ('onorientationchange' in window) {
      window.removeEventListener(
        'orientationchange',
        this.handleOrientationChange,
        false
      );
    }
  }

  handleOrientationChange() {
    // Adding some delay to this logic otherwise it doesn't
    // have enough time to detect the new window size
    setTimeout(() => {
      this.setState({
        isRecircVisible: !isMobile(1023)
      });
    }, 500);
  }

  handleNavMouseEnter(e) {
    // mouse enter/leave events fire on touch devices and we don't want to
    // double-trigger opening the tab
    if (isTouchDevice) {
      return;
    }

    this.openTab(getTabLabel(e));
  }

  handleNavMouseLeave() {
    // mouse enter/leave events fire on touch devices and we don't want to
    // double-trigger closing the tab
    if (isTouchDevice) {
      return;
    }
    this.closeTab();
  }

  handleNavClick(e) {
    // the 'more' section does not have an actual page, so stop the event and
    // keep the tab open
    if (e.target.getAttribute('href') === '#') {
      e.preventDefault();
      return;
    }

    // close the tab on click so user can see the "loading" message and it looks
    // like we're going somewhere. no need to mess with the event here because
    // it's a link.
    this.closeTab();
  }

  handleNavTouchStart(e) {
    const tabLabel = getTabLabel(e);

    if (tabLabel !== this.props.activeTab) {
      this.openTab(tabLabel);
    } else {
      this.closeTab();
    }
  }

  // eslint-disable-next-line class-methods-use-this
  handleNavTouchEnd(e) {
    // preventDefault() is necessary for some browsers (mobile safari < 13,
    // chrome mobile) in addition to stopPropagation(). this prevents us from
    // registering a tap/click on the main nav bar the normal way.
    e.preventDefault();

    // stop the touch event so the click event doesn't fire
    e.stopPropagation();
  }

  handleNavItemClick() {
    // close the tab on click so user can see the "loading" message and it looks
    // like we're going somewhere. no need to mess with the event here because
    // it's a link.
    this.closeTab();
  }

  // eslint-disable-next-line class-methods-use-this
  handleNavItemTouchStart(e) {
    // stop the touch event so the click event doesn't fire
    e.stopPropagation();
  }

  handleCloseClick() {
    this.closeTab();
  }

  closeTab() {
    const { activeTab, setActiveTab } = this.props;

    clearTimeout(this.openActiveTabTimeout);
    this.openActiveTabTimeout = null;

    // no need to setState if we don't have to
    if (activeTab === null) {
      return;
    }

    setActiveTab(null);
    document.body.classList.remove('with-open-nav');
  }

  openTab(tabLabel) {
    const { activeTab } = this.props;

    // no need to setState if we don't have to
    if (!tabLabel || tabLabel === activeTab) {
      return;
    }

    if (!activeTab && !isTouchDevice) {
      this.openActiveTabTimeout = setTimeout(() => {
        this.openActiveTab(tabLabel);
        /*
          the duplication of `document.body.classList.add('with-open-nav')` here
          is necessary because we only want to add the body class at the time we're setting
          the active tab with `setActiveTab`.
          This prevents a bug where the body class was added (preventing scrolling)
          but the activeTab wasn't set (because of the delay) which resulted in the
          body class NOT being removed and preventing the user from scrolling altogether
        */

        document.body.classList.add('with-open-nav');
      }, SiteHeaderNav.OPEN_ACTIVE_TAB_TIMEOUT_MS);
    } else {
      this.openActiveTab(tabLabel);
      document.body.classList.add('with-open-nav');
    }
  }

  openActiveTab = tabLabel => {
    const { activatedTabs } = this.state;
    const { setActiveTab } = this.props;
    if (activatedTabs[tabLabel]) {
      setActiveTab(tabLabel);
    } else {
      this.setState(
        state => ({
          activatedTabs: state.activatedTabs.set(tabLabel, true)
        }),
        () => {
          setActiveTab(tabLabel);
        }
      );
    }
  };

  render() {
    const { navTree, activeTab } = this.props;
    const { activatedTabs, isRecircVisible } = this.state;

    return (
      <Fragment>
        <ul className="SiteHeaderNav">
          {navTree.map(({ label, href, as, sections, recirc }) => (
            <li
              className={`SiteHeaderNav__item ${
                activeTab === label ? 'SiteHeaderNav__item--active' : ''
              }`}
              key={label}
              data-tab-label={label}
              onMouseEnter={this.handleNavMouseEnter}
              onMouseLeave={this.handleNavMouseLeave}
            >
              <Link href={href} as={as && as}>
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <a
                  className="SiteHeaderNav__link"
                  data-tab-label={label}
                  data-gtm-nav-category={label}
                  onClick={sections && this.handleNavClick}
                  onTouchStart={sections && this.handleNavTouchStart}
                  onTouchEnd={sections && this.handleNavTouchEnd}
                >
                  {label}
                </a>
              </Link>

              {sections && (
                <div
                  className={`SiteHeaderNav__flyout ${
                    activeTab === label ? 'SiteHeaderNav__flyout--active' : ''
                  }`}
                >
                  <SiteHeaderNavBlock
                    sections={sections}
                    parent={{ label, href, as }}
                    handleNavClick={this.handleNavClick}
                    handleNavItemClick={this.handleNavItemClick}
                  />

                  {recirc && activatedTabs.has(label) && isRecircVisible && (
                    <div className="SiteHeaderNav__recirc">
                      <Fragment>
                        {recirc.map(r => {
                          const recircProps = {
                            name: 'nav__popular',
                            design: 'text',
                            limit: 3
                          };

                          const props = {
                            ...recircProps,
                            ...r
                          };

                          return (
                            <Fragment key={r.title}>
                              {r.categories ? (
                                <Recirc {...props} />
                              ) : (
                                <SpreadBundle {...navRecirc[label][r.title]} />
                              )}
                            </Fragment>
                          );
                        })}
                      </Fragment>
                    </div>
                  )}

                  <div className="SiteHeaderNav__close-flyout">
                    <button onClick={this.handleCloseClick}>
                      <Arrow rotate={270} />
                      Close
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}

          <li
            className={`SiteHeaderNav__item ${
              activeTab === 'more' ? 'SiteHeaderNav__item--active' : ''
            }`}
            data-tab-label="more"
            onMouseEnter={this.handleNavMouseEnter}
            onMouseLeave={this.handleNavMouseLeave}
          >
            <a
              href="#"
              className="SiteHeaderNav__link"
              data-tab-label="more"
              data-gtm-nav-category="More"
              onClick={this.handleNavClick}
              onTouchStart={this.handleNavTouchStart}
              onTouchEnd={this.handleNavTouchEnd}
            >
              More
            </a>

            <div
              className={`SiteHeaderNav__flyout SiteHeaderNav__flyout--more ${
                activeTab === 'more' ? 'SiteHeaderNav__flyout--active' : ''
              }`}
            >
              {FEATURE_LINKS.length > 0 && activatedTabs.has('more') && (
                <div
                  className={`SiteHeaderNav__block SiteHeaderNav__featuresBlock SiteHeaderNav__featuresBlock--${FEATURE_LINKS.length}`}
                >
                  <p className="SiteHeaderNav__featuresHeading">
                    {SITE_HEADER_FEATURES_HEADING}
                  </p>
                  {FEATURE_LINKS.map(
                    ({ narrowImageUrl, wideImageUrl, href, as }) => {
                      const linkProps = {
                        href,
                        ...(as && { as })
                      };
                      return (
                        <Link {...linkProps} key={href}>
                          <a className="SiteHeaderNav__feature">
                            <Image
                              className="SiteHeaderNav__featureImage SiteHeaderNav__featureImage--narrow"
                              url={narrowImageUrl}
                              sourceSizes={
                                FEATURES_SOURCE_SIZES[FEATURE_LINKS.length - 1]
                              }
                              maxSize={{
                                width: 90,
                                aspectRatio: 0.6
                              }}
                            />
                            <Image
                              className="SiteHeaderNav__featureImage SiteHeaderNav__featureImage--wide"
                              url={wideImageUrl}
                              sourceSizes={
                                FEATURES_SOURCE_SIZES[FEATURE_LINKS.length - 1]
                              }
                              maxSize={{
                                width: 300,
                                aspectRatio: 2
                              }}
                            />
                          </a>
                        </Link>
                      );
                    }
                  )}
                </div>
              )}

              <div className="SiteHeaderNav__more-links">
                {MORE_SECTIONS.map(({ sectionName, sectionLinks }) => (
                  <div key={sectionName} className="SiteHeaderNav__block">
                    <p className="SiteHeaderNav__block-heading">
                      {sectionName}
                    </p>
                    <ul className="SiteHeaderNav__block-links">
                      {sectionLinks.map(({ label, href, as }) => {
                        const linkProps = {
                          href,
                          ...(as && { as })
                        };
                        return (
                          <li key={label}>
                            <Link {...linkProps}>
                              <a
                                className="SiteHeaderNav__block-link"
                                data-gtm-nav-category="More"
                                data-gtm-nav-section="Read"
                                data-gtm-nav-item={label}
                                onClick={this.handleNavItemClick}
                                onTouchStart={this.handleNavItemTouchStart}
                              >
                                {label}
                              </a>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="SiteHeaderNav__block SiteHeaderNav__networkBlock">
                <p className="SiteHeaderNav__block-heading">Follow Us</p>
                <div className="SiteHeaderNav__social-links">
                  {SOCIAL_LINKS.map(({ label, href }) => (
                    <div className="SiteHeaderNav__social-link" key={label}>
                      <ActionIcon
                        target={label !== 'email' ? '_blank' : undefined}
                        type={label}
                        url={href}
                        variant="knockout"
                      />
                    </div>
                  ))}
                </div>
                <p className="SiteHeaderNav__block-heading">
                  Visit our partner site
                </p>
                <LogoLink
                  href={X_SITE}
                  logo={X_SITE_LOGO}
                  className="SiteHeaderNav__xsite-logo"
                  gtmAttrs={{
                    'data-gtm-nav-category': 'More',
                    'data-gtm-nav-section': 'Company',
                    'data-gtm-nav-item': X_SITE_NAME
                  }}
                />
                <p className="SiteHeaderNav__block-text">
                  {X_SITE_DESCRIPTION}
                </p>
              </div>

              <div className="SiteHeaderNav__close-flyout">
                <button onClick={this.handleCloseClick}>
                  <Arrow rotate={270} />
                  Close
                </button>
              </div>
            </div>
          </li>
        </ul>
        <style jsx>{siteHeaderNavStyles}</style>
      </Fragment>
    );
  }
}
SiteHeaderNav.OPEN_ACTIVE_TAB_TIMEOUT_MS = 100;

SiteHeaderNav.propTypes = {
  navTree: arrayOf(
    shape({
      ...LINK_STRUCTURE,
      sections: arrayOf(
        shape({
          sectionName: string,
          sectionLinks: arrayOf(shape(LINK_STRUCTURE))
        })
      )
    })
  ).isRequired,
  activeTab: string,
  setActiveTab: func.isRequired
};

SiteHeaderNav.defaultProps = {
  activeTab: null
};

export default SiteHeaderNav;
